/**
 * Browser-level connectivity hooks (offline/online, tab visibility, window focus).
 * SignalR alone often misses iOS Safari backgrounding and airplane mode.
 * @param {{ onOffline?: () => void, onOnline?: () => void, onForeground?: (detail: { hiddenMs: number }) => void, onBackground?: () => void }} opts
 * @returns {() => void} dispose
 */
export function installHubConnectionMonitor({ onOffline, onOnline, onForeground, onBackground } = {}) {
  if (typeof window === 'undefined') return () => {};

  let hiddenAt = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let foregroundTimer = null;

  const fireForeground = () => {
    if (document.visibilityState && document.visibilityState !== 'visible') return;
    if (foregroundTimer) clearTimeout(foregroundTimer);
    foregroundTimer = setTimeout(() => {
      foregroundTimer = null;
      const hiddenMs = hiddenAt > 0 ? Math.max(0, Date.now() - hiddenAt) : 0;
      onForeground?.({ hiddenMs });
    }, 50);
  };

  const onVis = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      onBackground?.();
      return;
    }
    if (document.visibilityState === 'visible') fireForeground();
  };

  const onOfflineEv = () => onOffline?.();
  const onOnlineEv = () => onOnline?.();

  const onPageShow = (ev) => {
    if (ev.persisted) fireForeground();
  };
  const onFocus = () => fireForeground();

  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('focus', onFocus);
  window.addEventListener('offline', onOfflineEv);
  window.addEventListener('online', onOnlineEv);

  return () => {
    if (foregroundTimer) clearTimeout(foregroundTimer);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('offline', onOfflineEv);
    window.removeEventListener('online', onOnlineEv);
  };
}
